import { useCallback, useEffect, useRef, useState } from 'react'

import { useReducedMotion } from '../useReducedMotion/useReducedMotion'

const PHRASES = [
  'Support mental health programs',
  'Help protect wildlife',
  'Support food banks near me',
  'Help disaster relief efforts',
  'Help refugees'
]

const TYPING_SPEED_MS = 50
const PAUSE_MS = 1800
const ERASE_SPEED_MS = 30

export function useAnimatedPlaceholder(value: string, enabled = true) {
  const [animatedPlaceholder, setAnimatedPlaceholder] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const phraseIndexRef = useRef(0)
  const charIndexRef = useRef(0)
  const isErasingRef = useRef(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reducedMotion = useReducedMotion()

  const isActive = enabled && value === '' && !reducedMotion
  const isAnimating = isActive

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const animate = useCallback(() => {
    const phrase = PHRASES[phraseIndexRef.current]

    if (!isErasingRef.current) {
      if (charIndexRef.current < phrase.length) {
        charIndexRef.current += 1
        setAnimatedPlaceholder(phrase.slice(0, charIndexRef.current))
        timerRef.current = setTimeout(animate, TYPING_SPEED_MS)
      } else {
        timerRef.current = setTimeout(() => {
          isErasingRef.current = true
          animate()
        }, PAUSE_MS)
      }
    } else if (charIndexRef.current > 0) {
      charIndexRef.current -= 1
      setAnimatedPlaceholder(phrase.slice(0, charIndexRef.current))
      timerRef.current = setTimeout(animate, ERASE_SPEED_MS)
    } else {
      isErasingRef.current = false
      phraseIndexRef.current = (phraseIndexRef.current + 1) % PHRASES.length
      timerRef.current = setTimeout(animate, TYPING_SPEED_MS)
    }
  }, [])

  useEffect(() => {
    if (isActive) {
      clearTimer()
      phraseIndexRef.current = 0
      charIndexRef.current = 0
      isErasingRef.current = false
      setAnimatedPlaceholder('')
      timerRef.current = setTimeout(animate, TYPING_SPEED_MS)
    } else {
      clearTimer()
      setAnimatedPlaceholder('')
    }

    return clearTimer
  }, [isActive, animate, clearTimer])

  const onFocus = useCallback(() => setIsFocused(true), [])
  const onBlur = useCallback(() => setIsFocused(false), [])

  return { animatedPlaceholder, isAnimating, isFocused, onFocus, onBlur }
}
